import { html } from '@shared/utils/rendering/html';
import { useEffect, useRef, useState } from 'preact/hooks';
import AppLayout from '../../components/app-layout';
import { useLocation } from 'preact-iso';
import LoadingScreen from './mfe-screens/loading-screen';
import ErrorScreen from './mfe-screens/error-screen';
import OfflineScreen from './mfe-screens/offline-screen';
import { useSystemStatus } from '../../hooks/useSystemStatus';

// Helper to dynamically inject a remoteEntry script for a federated module
function ensureRemoteEntry(remoteGlobal, remoteBaseUrl) {
    if (typeof window === 'undefined') return Promise.reject(new Error('Not running in a browser'));
    if (window[remoteGlobal]) return Promise.resolve();

    if (!remoteBaseUrl)
        return Promise.reject(new Error(`Remote base URL not provided for ${remoteGlobal}`));

    const src = `${remoteBaseUrl.replace(/\/$/, '')}/dist/remoteEntry.js`;

    return new Promise((resolve, reject) => {
        // If a script with same src already exists, wait for it
        const existing = Array.from(document.getElementsByTagName('script')).find(
            (s) => s.src === src
        );
        if (existing) {
            if (window[remoteGlobal]) return resolve();
            existing.addEventListener('load', () => resolve());
            existing.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)));
            return;
        }

        const script = document.createElement('script');
        script.src = src;
        script.type = 'text/javascript';
        script.async = true;
        script.onload = () => {
            resolve();
        };
        script.onerror = () => {
            console.error(`[Portal] Failed to load remoteEntry: ${src}`);
            reject(new Error(`Failed to load ${src}`));
        };
        document.head.appendChild(script);
    });
}

const remoteConfigMap = {
    rdarr: { global: 'rdarrApp', envKey: 'RDARR_APP_URL', title: 'RDARR', icon: 'finance' },
    indicators: {
        global: 'indicatorsApp',
        envKey: 'INDICATOR_APP_URL',
        title: 'Indicators',
        icon: 'finance_mode',
    },
    ewis: {
        global: 'ewisApp',
        envKey: 'EWIS_APP_URL',
        title: 'EWIS',
        icon: 'crisis_alert',
    },
    riskAppetite: {
        global: 'riskAppetiteApp',
        envKey: 'RISK_APPETITE_APP_URL',
        title: 'Risk Appetite',
        icon: 'query_stats',
    },
};

async function withFallback(appKey, primaryImport) {
    try {
        return await primaryImport();
    } catch (err) {
        const { getConfig } = await import('../../../config.js');
        const cfg = await getConfig();

        const map = remoteConfigMap[appKey];
        const url = cfg[map?.envKey] || cfg[map?.envKey?.toLowerCase()];

        if (!url) {
            console.error(`[Portal] No runtime URL found for ${appKey}`);
            throw err;
        }

        await ensureRemoteEntry(map.global, url);

        const container = window[map.global];
        await container.init(__webpack_share_scopes__.default);
        const factory = await container.get('./Module');
        return factory();
    }
}

const loaders = {
    rdarr: () => withFallback('rdarr', () => import('rdarrApp/Module')),
    indicators: () => withFallback('indicators', () => import('indicatorsApp/Module')),
    ewis: () => withFallback('ewis', () => import('ewisApp/Module')),
    riskAppetite: () => withFallback('riskAppetite', () => import('riskAppetiteApp/Module')),
};

async function getRelayToken(appKey) {
    const response = await fetch('/api/relay/token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ app: appKey }),
    });

    if (!response.ok) throw new Error('Failed to mint relay token');
    return response.json(); // { relayToken, exp, jti }
}

export function MicroAppRoute({ appName, currentPath }) {
    const { url, route } = useLocation();
    const subPath = url.replace(currentPath, '') || '/';

    const [container, setContainer] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setLoading] = useState(false);

    // Check system status (polls every 30s internally)
    const { status: systemStatus, isLoading: statusLoading } = useSystemStatus(appName);

    // Track mount state to prevent the 30-second status poll from re-mounting
    // an already-live MFE. Reset when the remote goes offline.
    const hasMountedRef = useRef(false);
    const mountKeyRef = useRef(null);
    const remoteHandleRef = useRef(null);
    const disposedRef = useRef(false);

    // The remote stays mounted while the URL under it changes, so it has to be told the new path: the
    // path it is showing now, the path the URL says it should show, and the loaded module (needed to
    // mount again for a remote that can't be told).
    const mountedPathRef = useRef(null);
    const latestSubPathRef = useRef(subPath);
    latestSubPathRef.current = subPath;
    const remoteModuleRef = useRef(null);

    // Leaving the route must tear the remote down; otherwise its detached tree keeps running
    // and a later return to the route stacks a second live instance on top of it.
    useEffect(
        () => () => {
            disposedRef.current = true;
            Promise.resolve(remoteHandleRef.current)
                .then((handle) => handle?.unmount?.())
                .catch(() => {});
            remoteHandleRef.current = null;
        },
        []
    );

    const refCallback = (el) => {
        if (el && el !== container) {
            setContainer(el);
        }
    };

    const onNavigate = (nextPath) => {
        route(`${currentPath}${nextPath.startsWith('/') ? '' : '/'}${nextPath}`);
    };

    const reauthorize = async () => {
        const fresh = await getRelayToken(appName);
        return fresh.token;
    };

    // Mounts the remote at `path`. The handle is stored straight away (as a promise) so that anything
    // that runs while the relay token is still being fetched waits for this mount instead of starting
    // another one.
    const mountRemote = (mod, path, relayTokenPromise) => {
        mountedPathRef.current = path;
        const handle = Promise.resolve(relayTokenPromise).then((relayToken) =>
            disposedRef.current
                ? null
                : mod.mount(container, {
                      basePath: currentPath.replace(/\/\*$/, ''),
                      path,
                      onNavigate,
                      relayToken,
                      reauthorize,
                      hostOrigin: window.location.origin,
                  })
        );
        remoteHandleRef.current = handle;
        return handle;
    };

    // Keeps the mounted remote on the path the URL says. A remote whose handle has update() is told the
    // new path in place - no relay exchange. One without it (a build older than this contract) is mounted
    // again, which is how every URL change used to be handled; that keeps the host safe to release
    // before the remotes.
    const syncRemotePath = () => {
        const pending = remoteHandleRef.current;
        if (!pending) return; // not mounted yet; the mount catches up once it completes

        Promise.resolve(pending)
            .then((handle) => {
                const target = latestSubPathRef.current;
                if (disposedRef.current || remoteHandleRef.current !== pending) return;
                if (mountedPathRef.current === target) return;

                if (typeof handle?.update === 'function') {
                    mountedPathRef.current = target;
                    handle.update(target);
                    return;
                }

                const mod = remoteModuleRef.current;
                if (!mod) return;
                handle?.unmount?.();
                const tokenPromise = getRelayToken(appName).then((relay) => relay.token);
                // Re-check afterwards: the URL may have moved on again while this mount was starting.
                mountRemote(mod, target, tokenPromise).then(syncRemotePath, () => {});
            })
            .catch((error) =>
                console.error('[Portal] keeping the remote on the current path failed:', error)
            );
    };

    // The URL under the remote changed: its own onNavigate, the browser's back/forward, or a link.
    useEffect(() => {
        syncRemotePath();
    }, [subPath]);

    useEffect(() => {
        if (!container || isLoading || statusLoading) return;

        // Do not probe a remote unless Portal has a status record for it.
        // Missing status means the system is not configured or unavailable, so fail closed.
        if (!systemStatus?.isActive) {
            hasMountedRef.current = false;
            mountKeyRef.current = null;
            return;
        }

        // Stable key representing which remote is mounted at which base route. Deliberately
        // excludes `url`/`subPath` — those change on every internal navigation *within* the
        // already-mounted remote (its own router calls `onNavigate`, which updates the host's
        // URL for deep-linking). Keying on the full `url` would remount the whole remote module
        // on every internal nav, which is exactly what caused intermittent "remote.mount is not
        // a function" failures when switching tabs inside an already-mounted remote.
        const mountKey = `${appName}::${currentPath}`;
        if (hasMountedRef.current && mountKeyRef.current === mountKey) return;

        hasMountedRef.current = true;
        mountKeyRef.current = mountKey;

        setLoading(true);
        setError(null);

        (async () => {
            try {
                const [mod, relay] = await Promise.all([
                    loaders[appName](),
                    getRelayToken(appName),
                ]);
                if (disposedRef.current) return;

                if (typeof mod.mount !== 'function') {
                    throw new TypeError('remote.mount is not a function');
                }

                remoteModuleRef.current = mod;
                // Mount at the path the URL shows *now* - it may have changed while the module and token
                // loaded - then catch up on anything that changes while the remote itself starts.
                mountRemote(mod, latestSubPathRef.current, relay.token).then(
                    syncRemotePath,
                    (mountError) => {
                        console.error('[Portal] mount failed:', mountError);
                        if (!disposedRef.current) {
                            hasMountedRef.current = false;
                            setError(mountError);
                        }
                    }
                );
            } catch (error) {
                console.error('[Portal] mount failed:', error);
                if (!disposedRef.current) {
                    hasMountedRef.current = false;
                    setError(error);
                }
            } finally {
                if (!disposedRef.current) setLoading(false);
            }
        })();
        // `url`/`subPath` intentionally excluded: a URL change must not mount the remote again (each
        // mount is a relay exchange). syncRemotePath() moves the mounted remote to the new path instead.
    }, [appName, currentPath, container, systemStatus, statusLoading]);

    // Show offline screen if system is inactive or has no status record.
    if (!statusLoading && (!systemStatus || !systemStatus.isActive)) {
        return html`
            <${AppLayout}>
                <${OfflineScreen}
                    systemName=${remoteConfigMap[appName]?.title || appName}
                    message=${systemStatus?.maintenanceMessage || 'System is currently unavailable.'}
                    estimatedReturn=${systemStatus?.estimatedReturnUtc}
                />
            </${AppLayout}>
        `;
    }

    let content;

    if (error) {
        content = html`<${ErrorScreen} error=${error} details=${remoteConfigMap[appName]} />`;
    } else {
        content = html`
            <div id=${appName} ref=${refCallback}></div>
            ${(isLoading || statusLoading) && html`<${LoadingScreen} details=${remoteConfigMap[appName]} />`}
        `;
    }

    return html`
        <${AppLayout}>
            ${content}
        </${AppLayout}>
    `;
}
